import { Button as AntButton, Flex } from 'antd';
import React from 'react';
import styled from 'styled-components';
import { theme } from '../../theme/semanticTokens';
import { Typography } from '../../Typography';

export interface FilterItem {
  id: string;
  text: string;
  active?: boolean;
  disabled?: boolean;
}

export interface FilterGroupProps {
  filters: FilterItem[];
  onFilterClick?: (filterId: string) => void;
  className?: string;
}

const StyledFilterGroup = styled(Flex)({
  alignItems: 'center',
  gap: theme.dime.spacing.l,
  flexWrap: 'wrap'
});

const filterVariants = {
  active: {
    base: theme.dime.colors.action.primary,
    hover: theme.dime.colors.action.primaryHover,
    pressed: theme.dime.colors.action.primaryPressed,
    text: theme.dime.colors.text.inverse
  },
  inactive: {
    base: theme.dime.colors.fill.secondary,
    hover: theme.dime.colors.surface.secondaryHover,
    pressed: theme.dime.colors.surface.secondaryPressed,
    text: theme.dime.colors.text.brand,
    hoverText: theme.dime.colors.text.brandHover
  }
} as const;

const ContentFilterTag = styled(AntButton)<{ $active: boolean }>(
  ({ $active }) => {
    const variant = $active ? filterVariants.active : filterVariants.inactive;

    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.dime.spacing.s,
      padding: `${theme.dime.spacing.s} ${theme.dime.spacing.l}`,
      borderRadius: theme.dime.radius.l,
      fontFamily: theme.dime.fonts.families.roboto,
      fontSize: theme.dime.fonts.sizes.sm,
      fontWeight: theme.dime.fonts.weights.regular,
      lineHeight: theme.dime.fonts.lineHeights.md,
      border: 'none',
      cursor: 'pointer',
      userSelect: 'none',
      transition: 'background-color 0.2s ease, color 0.2s ease',
      height: 'auto',
      boxShadow: 'none',
      backgroundColor: variant.base,
      color: variant.text,
      borderColor: $active ? variant.base : 'transparent',
      '&:hover:not(:disabled)': {
        backgroundColor: variant.hover,
        borderColor: variant.hover,
        color: $active ? variant.text : filterVariants.inactive.hoverText,
        '& *': {
          color: $active ? variant.text : filterVariants.inactive.hoverText
        }
      },
      '&:focus:not(:disabled)': {
        backgroundColor: variant.hover,
        borderColor: variant.hover,
        color: $active ? variant.text : filterVariants.inactive.hoverText
      },
      '&:active:not(:disabled)': {
        backgroundColor: variant.pressed,
        borderColor: variant.pressed,
        color: $active ? variant.text : filterVariants.inactive.hoverText
      },
      '&:disabled': {
        backgroundColor: theme.dime.colors.fill.secondary,
        color: theme.dime.colors.text.disabled,
        borderColor: 'transparent'
      }
    };
  }
);

const FilterText = styled(Typography)({
  color: 'inherit',
  transition: 'color 0.2s ease'
});

export const FilterGroup: React.FC<FilterGroupProps> = ({
  filters,
  onFilterClick,
  className = ''
}) => {
  if (filters.length === 0) {
    return null;
  }

  const handleFilterClick = (filterId: string) => {
    onFilterClick?.(filterId);
  };

  return (
    <StyledFilterGroup className={className} align="center">
      {filters.map((filter) => (
        <ContentFilterTag
          key={filter.id}
          $active={filter.active ?? false}
          disabled={filter.disabled ?? false}
          onClick={() => !filter.disabled && handleFilterClick(filter.id)}
        >
          <FilterText variant="body1">{filter.text}</FilterText>
        </ContentFilterTag>
      ))}
    </StyledFilterGroup>
  );
};
